import React, { useContext, useEffect, useRef, useState } from "react";
import { ThemeConsumer } from "styled-components";

import { CopyrightOverlay } from "@/common/CopyrightOverlay";
import { Footer } from "@/common/Footer";
import { Intro } from "@/common/Intro";
import { renderOutro } from "@/common/Outro";
import Slider, { SlideProps, SliderActions } from "@/common/Slider";
import Header from "@/components/Header";
import { SharingType } from "@/components/Sharing";
import { Subline } from "@/components/Subline";
import { PollProps } from "@/containers/Poll/PollContainer";
import { Group, Relative, Stage, Wrapper } from "@/containers/styles";
import { GlobalContext } from "@/contexts";
import PollContext from "@/contexts/PollContext";
import LocaleContext from "@/i18n";
import EventSystem from "@/service/EventSystem";
import IFrameService from "@/service/IFrame";
import InteractionService from "@/service/Interaction";
import {
  InteractionIntro,
  OutroType,
  PollResult,
  PollVote,
  Slide,
} from "@/service/Model";
import { getSharingTitle, handleShare } from "@/utils/socialActions";

// Localized components
import { PollBody } from "./PollBody";
import { PollMessage } from "./PollMessage";
import { PollOptions } from "./PollOptions";
import { PollSummary } from "./PollSummary";

const INTRO_SLIDE_KEY = "intro";
const NOTICE_SLIDE_KEY = "notice";
const SUMMARY_SLIDE_KEY = "summary";
const SUMMARY_SECRET_SLIDE_KEY = "summary_secret";

const ClassicPoll: React.FC<PollProps> = (props: PollProps): JSX.Element => {
  const globalContext = useContext(GlobalContext);
  const localeContext = useContext(LocaleContext);
  const pollContext = useContext(PollContext);

  const {
    interaction,
    interactionResults,
    votedSlideIds,
    completed,
    expired,
    votes,
    selection,
    stoppedAtSlide,
    totalVoteCount,
    userVoteCount,
  } = props;

  const [showTitle, setShowTitle] = useState<boolean>(false);
  const [showOutro, setShowOutro] = useState<boolean>(false);
  const laHyperScrollFinishedRef = useRef(false);

  // merge overlay states into one state?
  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const [closableOverlay, setClosableOverlay] = useState<boolean>(false);
  const [overlayMessage, setOverlayMessage] = useState<string>("");
  // rename dismiss to autoFadeOut?
  const [dismissOverlay, setDismissOverlay] = useState<boolean>(false);

  const [title, setTitle] = useState<string>("");
  const [footNote, setFootNote] = useState<string>("");
  const [showImageCopyrights, setShowImageCopyrights] = useState<boolean>(
    false
  );
  const [navigation, setNavigation] = useState<{
    previous: boolean;
    next: boolean;
  }>({
    previous: true,
    next: true,
  });

  const imagesData = InteractionService.getImagesData(interaction);

  useEffect(() => {
    // Register on container when scroll transition ended
    IFrameService.hasScrolledListener();

    // This event is triggered by la-hyper
    EventSystem.subscribe("parent:scroll:finished", () => {
      laHyperScrollFinishedRef.current = true;
      setTimeout(() => handleCustomSlideChange(SliderActions.next), 300);
    });

    if (expired && !interaction.secretPoll) {
      setShowOverlay(true);
      setOverlayMessage(localeContext.translation.endedPoll);
      setClosableOverlay(true);
      setDismissOverlay(false);
    }
  }, []);

  const handleCustomSlideChange = (
    direction: SliderActions,
    slideIndex?: number
  ) => {
    IFrameService.reportAnswerAction();
    EventSystem.publish(`slider:action:${direction}`, slideIndex);
  };

  const handleSlideChange = (
    nextSlide: SlideProps,
    hasPrevious: boolean,
    hasNext: boolean
  ): void => {
    const { translation } = localeContext;
    const totalQuestionSlides = interaction.slides.length;
    const questionSlideIndex: number = interaction.slides.findIndex(
      (slide: Slide) => slide.id === nextSlide.key
    );

    setTitle(nextSlide.meta?.title || "");
    setShowTitle(true);
    setNavigation({ previous: hasPrevious, next: hasNext });
    setFootNote(`${questionSlideIndex + 1}/${totalQuestionSlides}`);

    switch (nextSlide.key) {
      case NOTICE_SLIDE_KEY:
      case INTRO_SLIDE_KEY:
        setFootNote("");
        break;
      case SUMMARY_SLIDE_KEY:
        setFootNote(translation.result);
        setTitle(interaction.evaluationText || translation.pollSummary.title);
        setOverlayMessage(translation.thankYou);
        setShowOverlay(true);
        setDismissOverlay(false);
        setClosableOverlay(false);
        break;
      case SUMMARY_SECRET_SLIDE_KEY:
        setFootNote(translation.result);
        setTitle(interaction.evaluationText || translation.thankYou);
        break;
      default:
        break;
    }
  };

  const renderSlider = (): JSX.Element => {
    return (
      <ThemeConsumer>
        {(theme: ITheme) => {
          return (
            <Slider
              timing={500}
              selected={stoppedAtSlide}
              slides={slides}
              backgroundImageUrl={interaction.background}
              backgroundColor={
                interaction.backgroundColor || theme.colors.sliderBackground
              }
              isSlideVoted={(slideId: string) =>
                votedSlideIds.includes(slideId)
              }
              onSlideWillChange={(index: number) => {
                // fadeOut animations
                if (!slides[index]) {
                  return;
                }
                setShowTitle(false);
                setShowOutro(false);
                // Rename dismissOverlay to autoFadeOutOverlay?
                setDismissOverlay(showOverlay);
              }}
              onSlideChanged={(index: number) => {
                // fadeIn animations
                if (!slides[index]) {
                  return;
                }
                const currentSlide = slides[index];
                let hasPrevious = false,
                  hasNext = false;

                if (completed) {
                  hasPrevious = index > 0;
                  hasNext =
                    index < slides.length - 1 &&
                    (expired || votedSlideIds.includes(currentSlide.key));
                }

                handleSlideChange(currentSlide, hasPrevious, hasNext);
              }}
              onEnd={() => {
                // Trigger outro on last slide - no summary slide for single slide poll
                // see https://as-jira.axelspringer.de/browse/SPR-2779
                if (interaction.outro && interaction.slides.length === 1) {
                  setShowOutro(true);
                }
              }}
            />
          );
        }}
      </ThemeConsumer>
    );
  };

  const renderSliderOverlays = (): JSX.Element => {
    return (
      <React.Fragment>
        {showOverlay && (
          <Subline
            overlay={true}
            dismiss={dismissOverlay}
            showCloseButton={closableOverlay}
            onClose={() => {
              setShowOverlay(false);
              setOverlayMessage("");
              setDismissOverlay(false);
              setClosableOverlay(false);
            }}
          >
            {overlayMessage}
          </Subline>
        )}
        {showImageCopyrights && (
          <CopyrightOverlay
            onClose={() => setShowImageCopyrights(false)}
            imagesData={imagesData}
          />
        )}
      </React.Fragment>
    );
  };

  const renderPoll = (): JSX.Element => {
    const { participantsCount } = interactionResults;
    const { votes } = pollContext;

    const voted = votes.length > 0;
    const participantsCountPlusVote = voted
      ? participantsCount + 1
      : participantsCount;

    return (
      <Wrapper>
        <Header
          icons={globalContext.icons}
          visible={showTitle}
          navigation={navigation}
          onNavigationClick={(
            e: React.MouseEvent,
            direction: SliderActions
          ) => {
            handleCustomSlideChange(direction);
          }}
        >
          {title}
        </Header>
        <Stage>
          <Group>
            <Relative>
              {renderSlider()}
              {renderSliderOverlays()}
            </Relative>
            <Footer
              participants={participantsCountPlusVote}
              hideParticipantsCount={interaction.hideParticipantsCount}
              onCopyrightClick={() => setShowImageCopyrights(true)}
              hasCopyrights={imagesData.length > 0}
              leftText={footNote}
              icons={globalContext.icons}
              privacyPolicyLink={interaction.privacyPolicyLink1plusX}
              onShareClick={(type: SharingType) => {
                const sharingTitle = getSharingTitle(interaction);
                handleShare(
                  type,
                  sharingTitle,
                  globalContext.brandSettings.xHandle
                );
              }}
            />
          </Group>
        </Stage>
      </Wrapper>
    );
  };

  const renderOutroOverlay = (): JSX.Element | null => {
    const outro = props.interaction.outro;

    if (!outro || !showOutro) {
      return null;
    }

    return renderOutro(outro, { globals: globalContext }, () =>
      setShowOutro(!showOutro)
    );
  };

  const handleNextSlide = (delay: number, branch?: string): void => {
    const changeSlide = (): void => {
      const slideIndex = branch
        ? slides.findIndex((slide: SlideProps) => slide.key.includes(branch))
        : -1;

      if (slideIndex > -1) {
        handleCustomSlideChange(SliderActions.goto, slideIndex);
      } else {
        handleCustomSlideChange(SliderActions.next);
      }
    };

    setTimeout(() => {
      if (globalContext.hyperAvailable) {
        laHyperScrollFinishedRef.current = false;
        IFrameService.scroll();
        // la-hyper scroll:finished event received? (see useEffect)
        // If not slide manually after timeout
        setTimeout(() => {
          if (!laHyperScrollFinishedRef.current) {
            laHyperScrollFinishedRef.current = true;
            changeSlide();
          }
        }, 2000);
      } else {
        changeSlide();
      }
    }, delay);
  };

  const generatePollSlides = (): SlideProps[] => {
    const defaultBackground = interaction.background;
    const defaultBackgroundColor = interaction.backgroundColor;

    return interaction.slides.map((slide: Slide, index: number) => {
      const filteredResults: PollResult[] = interactionResults.results.filter(
        (pollResult: PollResult) => pollResult.slideId === slide.id
      );
      const userVotes = votes.filter(
        (vote: PollVote) => slide.id === vote.slideId
      );
      // Filter NOT confirmed slide votes (local storage)
      const selectedVotes = selection.filter(
        (vote: PollVote) => slide.id === vote.slideId
      );

      return {
        key: `${slide.id}`,
        meta: { title: slide.title },
        backgroundImageUrl: slide.background || defaultBackground,
        backgroundColor: defaultBackgroundColor,
        centerize: true,
        children: (
          <PollBody
            questionImage={slide.questionImage}
            multipleChoice={slide.multipleChoice}
          >
            <PollOptions
              key={index}
              results={filteredResults}
              interactionId={interaction.id}
              slide={slide}
              userVotes={[...userVotes, ...selectedVotes]}
              isFinished={userVotes.length > 0}
              isSecret={interaction.secretPoll}
              layout={interaction.layout}
              isEnded={expired}
              onNext={handleNextSlide}
            />
          </PollBody>
        ),
      };
    });
  };

  const generateIntroSlide = (intro: InteractionIntro): SlideProps => {
    const backgroundImageUrl = intro.background || interaction.background;
    return {
      key: INTRO_SLIDE_KEY,
      meta: { title: intro.title },
      backgroundImageUrl,
      backgroundColor: interaction.backgroundColor,
      children: (
        <Intro
          text={intro.text}
          label={intro.label}
          onButtonClick={() => handleNextSlide(0)}
          hasBackgroundImage={!!backgroundImageUrl}
        />
      ),
    };
  };

  const generateSummarySlide = (): SlideProps => {
    // Add Poll Summary
    if (interaction.secretPoll) {
      const { endDate, evaluationTextarea } = interaction;
      const {
        translation: { secretSummary: i18n },
      } = localeContext;
      let text = i18n.defaultText;
      if (endDate) {
        const date = new Date(endDate);
        date.setDate(date.getDate() + 1);
        text = i18n.defaultTextWithEndDate(date.toLocaleDateString());
      }

      return {
        key: SUMMARY_SECRET_SLIDE_KEY,
        backgroundImageUrl: interaction.background,
        backgroundColor: interaction.backgroundColor,
        children: (
          <PollMessage
            text={evaluationTextarea || text}
            onLoad={() => setShowOutro(true)}
          />
        ),
      };
    }

    const onCtaClick =
      interaction.outro?.outroType === OutroType.RegistrationForm
        ? () => setShowOutro(true)
        : undefined;

    return {
      key: SUMMARY_SLIDE_KEY,
      backgroundImageUrl: interaction.background,
      backgroundColor: interaction.backgroundColor,
      children: (
        <PollSummary
          icons={globalContext.icons}
          showDetails={
            interaction.slides.length === 1 &&
            !interaction.hideParticipantsCount
          }
          totalVoteCount={totalVoteCount}
          userVoteCount={userVoteCount}
          sharingTitle={getSharingTitle(interaction)}
          onCtaClick={onCtaClick}
          onLoad={() => setShowOutro(true)}
        />
      ),
    };
  };

  const generateClosedPollSlide = (
    messageTitle: string,
    message: string
  ): SlideProps => ({
    key: NOTICE_SLIDE_KEY,
    meta: { title: messageTitle },
    backgroundImageUrl: interaction.background,
    backgroundColor: interaction.backgroundColor,
    children: <PollMessage text={message} />,
  });

  const generateSlides = (): SlideProps[] => {
    let slideProps: SlideProps[] = [];

    if (interaction.secretPoll && expired) {
      const {
        secretSummary: { surveyEndedTitle, surveyEndedMessage },
      } = localeContext.translation;
      return [generateClosedPollSlide(surveyEndedTitle, surveyEndedMessage)];
    }

    // Add poll intro
    if (interaction.intro) {
      slideProps.push(generateIntroSlide(interaction.intro));
    }

    // Add poll slides
    slideProps = [...slideProps, ...generatePollSlides()];

    // Add poll summary
    // (single slide and expired polls do not have a summary slide)
    if (interaction.slides.length > 1 && !expired) {
      slideProps.push(generateSummarySlide());
    }

    return slideProps;
  };

  const slides = generateSlides();

  return (
    <React.Fragment>
      {renderPoll()}
      {renderOutroOverlay()}
    </React.Fragment>
  );
};

export default ClassicPoll;
